import { AlertTriangle, FileWarning, Link2, MonitorCheck, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DocReviewReason } from '@features/document-capture/types/documentCapture.types';
import { useSystemHealthStore } from '../store/systemHealthStore';
import { StatKpiCard, type KpiTone } from './StatKpiCard';
import type { DocScanLaneState, DocScanSweepStage } from '../types/systemHealth.types';

/** Age past which the review backlog's oldest item turns amber. */
const REVIEW_SLA_HOURS = 48;

/**
 * 86d4a0z4z — the review queue's tabs, by `DocReviewKind` ordinal (0 Scan, 1 NetSuite, 2 Replacement).
 * Spelled out here rather than imported from the document-capture feature so the health panel keeps
 * rendering an unknown ordinal as "kind N" instead of blank if the server vocabulary grows first.
 */
const REVIEW_KIND_LABEL: Record<number, string> = {
  0: 'scans',
  1: 'NetSuite',
  2: 'replacements',
};

const REASON_LABEL: Record<number, string> = {
  [DocReviewReason.NoBarcode]: 'No barcode',
  [DocReviewReason.MultipleNumbers]: 'Multiple numbers',
  [DocReviewReason.NumberNotOnPage1]: 'Number not on page 1',
  [DocReviewReason.NonConsecutiveRepeat]: 'Non-consecutive repeat',
  [DocReviewReason.NeverAppearedInNs]: 'Never appeared in NetSuite',
  [DocReviewReason.MultipleNsMatches]: 'Multiple NetSuite matches',
  [DocReviewReason.WrongRecordFamily]: 'Wrong record family',
  [DocReviewReason.NsVoided]: 'NetSuite voided',
  [DocReviewReason.CustbodyConflict]: 'Custbody conflict',
  [DocReviewReason.ClosedPeriod]: 'Closed period',
  [DocReviewReason.LinkFailed]: 'Link failed',
  [DocReviewReason.FewerPages]: 'Fewer pages',
  [DocReviewReason.SupersedePending]: 'Supersede pending',
  [DocReviewReason.LinkBroken]: 'Link broken',
  [DocReviewReason.UnknownPrefix]: 'Unknown prefix',
  // 14ykpqtx1z9 — retired reason; the label stays so resolved items from the hold's lifetime still read.
  [DocReviewReason.AppliedCredits]: 'Journal has applied credits',
};

const LANE_LABEL: Record<DocScanLaneState, string> = {
  off: 'Off',
  pending: 'Pending',
  alerting: 'Alerting',
  healthy: 'Healthy',
};

const LANE_TONE: Record<DocScanLaneState, KpiTone> = {
  off: 'info',
  pending: 'warning',
  alerting: 'destructive',
  healthy: 'success',
};

/** Short, friendly stage names for the sweep stages (keys are the server StageKey). */
const SWEEP_STAGE_LABEL: Record<string, string> = {
  'docscan.sweep.reheal': '0-match self-heal',
  'docscan.sweep.unscanned': 'Unscanned paper',
  'docscan.sweep.refresh': 'Linked re-verify',
  'docscan.sweep.storediff': 'Store ↔ DB diff',
  // 14ykpqtx1z9 — drains the journals the retired applied-credits hold parked. Empty after its first run.
  'docscan.sweep.holdretire': 'JV hold retirement',
};

function formatHoursAge(h: number): string {
  if (h <= 0) return '0m';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${Math.round(h / 24)}d`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const hrs = Math.floor(m / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function sweepStageLabel(key: string): string {
  return SWEEP_STAGE_LABEL[key] ?? key.replace('docscan.sweep.', '');
}

function sweepBadge(s: DocScanSweepStage): { variant: 'success' | 'warning' | 'destructive' | 'outline'; text: string } {
  if (s.lastError) return { variant: 'destructive', text: 'error' };
  if (s.isStale) return { variant: 'warning', text: 'stale' };
  if (s.lastSuccessAt) return { variant: 'success', text: 'ok' };
  return { variant: 'outline', text: 'idle' };
}

/**
 * Document Capture health (86d3rrfee) — subscribes to the `docScanHealth` slice ONLY (fine-grained selector),
 * so its poll re-renders this panel without touching the trend charts. Four headline tiles (link lane, review
 * backlog, stations, sweep) over three detail cards (review by reason, stations, sweep stages).
 */
export function DocumentCaptureHealthPanel() {
  const health = useSystemHealthStore((s) => s.docScanHealth);

  if (!health) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-xl border p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    );
  }

  const { lane, reviewQueue, stations, sweep } = health;

  // ---- tile derivations ----
  const laneSubtitle =
    lane.state === 'off'
      ? 'Link lane disabled'
      : lane.state === 'pending'
        ? 'Awaiting first probe'
        : lane.state === 'alerting'
          ? `${lane.probeFailedFields.length} field(s) failing`
          : `${lane.probeFieldsChecked} field(s) OK`;

  const staleStations = stations.filter((s) => s.isStale).length;
  const silentBiz = stations.filter((s) => s.isSilentInBusinessHours).length;
  const stationsTone: KpiTone = silentBiz > 0 ? 'destructive' : staleStations > 0 ? 'warning' : 'success';

  const staleSweep = sweep.filter((s) => s.isStale).length;
  const erroredSweep = sweep.filter((s) => s.lastError).length;
  const sweepTone: KpiTone = erroredSweep > 0 ? 'destructive' : staleSweep > 0 ? 'warning' : sweep.length ? 'success' : 'info';

  const reviewTone: KpiTone =
    reviewQueue.maxAgeHours > REVIEW_SLA_HOURS ? 'warning' : reviewQueue.openTotal > 0 ? 'info' : 'success';

  // 86d4a0z4z — throughput beside the backlog. Rendered only when there is something to say: on a
  // quiet morning "0 raised today" is noise, and the backlog value above already says the queue is
  // clear. The per-tab split rides along so a spike can be attributed without opening the queue.
  const raisedTodayFooter =
    reviewQueue.raisedToday.total > 0
      ? `${reviewQueue.raisedToday.total} raised today` +
        (reviewQueue.raisedToday.byKind.length > 1
          ? ` (${reviewQueue.raisedToday.byKind
              .map((k) => `${REVIEW_KIND_LABEL[k.kind] ?? `kind ${k.kind}`} ${k.count}`)
              .join(', ')})`
          : '')
      : '';

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatKpiCard
          title="Link lane"
          titleTip="NetSuite link lane posture. 'Off' = disabled (NsLinkEnabled=false, expected before pilot); 'Healthy' = the daily custbody probe found every field queryable; 'Alerting' = a field is not queryable (link jobs will fail into review). Source: config + probe tracker."
          value={LANE_LABEL[lane.state]}
          subtitle={laneSubtitle}
          icon={Link2}
          tone={LANE_TONE[lane.state]}
          footer={
            lane.nsLinkEnabled && lane.nsLinkDryRun ? <span className="text-warning">Dry run — no writes</span> : undefined
          }
        />
        <StatKpiCard
          title="Review backlog"
          titleTip="Open review items across all reasons, and the age of the oldest. Turns amber past the 48h SLA. The footer counts items RAISED today (Philippine day), which is throughput rather than backlog — a queue that empties as fast as it fills reads as zero here otherwise. Source: DocumentReviewItems."
          value={reviewQueue.openTotal}
          subtitle={reviewQueue.openTotal > 0 ? `Oldest ${formatHoursAge(reviewQueue.maxAgeHours)}` : 'Queue clear'}
          icon={FileWarning}
          tone={reviewTone}
          valueClassName={reviewQueue.maxAgeHours > REVIEW_SLA_HOURS ? 'text-warning' : undefined}
          footer={
            raisedTodayFooter || reviewQueue.unknownPrefixOpen > 0 ? (
              <span className="text-muted-foreground">
                {raisedTodayFooter}
                {raisedTodayFooter && reviewQueue.unknownPrefixOpen > 0 ? ' · ' : ''}
                {reviewQueue.unknownPrefixOpen > 0 ? `${reviewQueue.unknownPrefixOpen} unknown prefix` : ''}
              </span>
            ) : undefined
          }
        />
        <StatKpiCard
          title="Scan stations"
          titleTip="Stations reporting a heartbeat, and how many are silent during business hours (Mon–Sat 07:00–19:00 PH). A silent station in business hours is the alert. Source: ScanStationHeartbeats."
          value={stations.length === 0 ? '0' : `${stations.length - staleStations}/${stations.length}`}
          subtitle={
            stations.length === 0
              ? 'No stations yet'
              : silentBiz > 0
                ? `${silentBiz} silent in business hours`
                : `${stations.length} online`
          }
          icon={MonitorCheck}
          tone={stationsTone}
          valueClassName={silentBiz > 0 ? 'text-destructive' : undefined}
        />
        <StatKpiCard
          title="Reconciliation sweep"
          titleTip="Nightly 02:00 PH self-heal + drift sweep. A stage with no clean pass in over 26h is stale. Source: DocScanSweepStates last-success per stage."
          value={sweep.length === 0 ? 'Not yet run' : `${sweep.length - staleSweep}/${sweep.length} fresh`}
          subtitle={
            sweep.length === 0
              ? 'Runs nightly'
              : erroredSweep > 0
                ? `${erroredSweep} stage error(s)`
                : staleSweep > 0
                  ? `${staleSweep} stage(s) stale`
                  : 'All stages current'
          }
          icon={RefreshCw}
          tone={sweepTone}
          valueClassName={erroredSweep > 0 ? 'text-destructive' : staleSweep > 0 ? 'text-warning' : undefined}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Review backlog by reason */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Review backlog by reason</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {reviewQueue.byReason.length === 0 ? (
              <p className="text-sm text-muted-foreground">No open review items.</p>
            ) : (
              reviewQueue.byReason.map((b) => (
                <div key={b.reason} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-foreground">{REASON_LABEL[b.reason] ?? `Reason ${b.reason}`}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums text-muted-foreground">{formatHoursAge(b.oldestAgeHours)}</span>
                    <Badge variant={b.oldestAgeHours > REVIEW_SLA_HOURS ? 'warning' : 'outline'}>{b.openCount}</Badge>
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Scan stations */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Scan stations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {stations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No stations have reported yet.</p>
            ) : (
              stations.map((st) => (
                <div key={st.stationId} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {st.isSilentInBusinessHours && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
                    <span className="truncate text-foreground" title={st.stationId}>
                      {st.stationId}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                    <span className="tabular-nums">{Math.round(st.decodeSuccessRate7d * 100)}%</span>
                    <span className={cn('tabular-nums', st.isStale && 'text-warning')}>{formatRelative(st.lastSeenAt)}</span>
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Reconciliation sweep stages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Reconciliation sweep</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sweep.length === 0 ? (
              <p className="text-sm text-muted-foreground">The nightly sweep has not run yet.</p>
            ) : (
              sweep.map((s) => {
                const badge = sweepBadge(s);
                return (
                  <div key={s.stageKey} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-foreground" title={s.checkpoint ?? undefined}>
                      {sweepStageLabel(s.stageKey)}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="tabular-nums text-muted-foreground">{formatRelative(s.lastSuccessAt)}</span>
                      <Badge variant={badge.variant}>{badge.text}</Badge>
                    </span>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
